import { Alert, Box, Collapse, Divider, Drawer, List } from '@mui/material';

import { tss } from 'tss-react/mui';

import { use, type RefObject } from 'react';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { Loading } from '../loading/Loading';

import { Logo } from '../logo/Logo';

import TheKeyLogo from '../../public/images/menu/TheKeyLogo.png';

import { Or2Image } from '../image/Or2Image';

import { Or2Link } from '../navigation/Or2Link';

import { useShortcuts } from './hooks/useShortcuts';
import { ShortcutIcon } from './ShortcutIcon';
import { Footer } from './Footer';

const KEY_LOGO_HEIGHT = 20;
const KEY_LOGO_WIDTH = 110;

const useStyles = tss.create(({ theme }) => ({
  logoContainer: { justifySelf: 'center', marginLeft: theme.spacing(1) },
  logoInner: {
    marginLeft: 'auto',
    cursor: 'pointer',
  },
  drawer: {
    marginTop: theme.spacing(1),
    boxShadow: theme.boxShadow,
    borderRight: `1px solid ${theme.cardBorder}`,
    borderTop: `1px solid ${theme.cardBorder}`,
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.white,
  },
  list: {
    paddingTop: theme.spacing(1),
    backgroundColor: theme.palette.common.white,

    borderBottomRightRadius: theme.shape.borderRadius,
    paddingBottom: theme.spacing(2),
  },
}));

interface Props {
  containerRef: RefObject<HTMLDivElement | null>;
  drawerWidth: number;
  smallScreenDrawerOpen: boolean;
  largeScreenDrawerOpen: boolean;
  onToggleDrawer: () => void;
}

export const AppDrawer = ({
  containerRef,
  drawerWidth,
  smallScreenDrawerOpen,
  largeScreenDrawerOpen,
  onToggleDrawer,
}: Props) => {
  const { isImpersonating, isSuperuser } = use(CurrentUserContext);

  const { classes } = useStyles();
  const { shortcuts, loading } = useShortcuts();

  const drawer = (
    <>
      <div className={classes.drawer} data-tour-id="app-drawer">
        <Box sx={{ justifySelf: 'center', paddingTop: 1 }}>
          <a href="https://schoolleaders.thekeysupport.com/?marker=fromGHheader">
            <Or2Image
              src={TheKeyLogo}
              alt={'The Key Logo'}
              height={KEY_LOGO_HEIGHT}
              width={KEY_LOGO_WIDTH}
              unoptimized
            />
          </a>
        </Box>
        <Divider />
        <div>
          <Or2Link href="/news">
            <div className={classes.logoContainer}>
              <div className={classes.logoInner}>
                <Logo />
              </div>
            </div>
          </Or2Link>
        </div>
        <Divider />

        <Collapse in={loading}>
          <div>
            <Loading />
          </div>
        </Collapse>
        {isImpersonating && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            You are in impersonation mode
          </Alert>
        )}
        {isSuperuser && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            You are logged in as a superuser
          </Alert>
        )}
        <List dense disablePadding className={classes.list}>
          {shortcuts.map((shortcut) => {
            return (
              <ShortcutIcon
                key={shortcut._id}
                shortcut={shortcut}
                onClick={() => {
                  if (smallScreenDrawerOpen) {
                    onToggleDrawer();
                  }
                }}
              />
            );
          })}
        </List>
        <Divider />
        <Footer />
      </div>
    </>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={smallScreenDrawerOpen}
        onClose={onToggleDrawer}
        ModalProps={{
          container: () => containerRef.current,
          disablePortal: true,
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'absolute',
          '& .MuiBackdrop-root': {
            position: 'absolute',
          },
          '& .MuiDrawer-paper': {
            position: 'absolute',
            boxSizing: 'border-box',
            width: drawerWidth,
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="persistent"
        sx={(theme) => ({
          display: { xs: 'none', md: 'block' },
          flexShrink: 0,
          width: largeScreenDrawerOpen ? drawerWidth : 0,
          overflow: 'hidden',
          transition: theme.transitions.create('width', {
            easing: largeScreenDrawerOpen
              ? theme.transitions.easing.easeOut
              : theme.transitions.easing.sharp,
            duration: largeScreenDrawerOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
          '& .MuiDrawer-paper': {
            position: 'relative',
            boxSizing: 'border-box',
            width: drawerWidth,
            border: 0,
            backgroundColor: 'transparent',
          },
        })}
        open={largeScreenDrawerOpen}
      >
        {drawer}
      </Drawer>
    </>
  );
};
